/**
 * Team management API functions.
 * Uses cpGuestApiCall for public endpoints (no JWT required).
 * Uses cpGuestApiCall with access_token in body for authenticated endpoints.
 */
import { cpGuestApiCall, getCPToken } from '@/lib/api/controlPlaneClient';
import type { CPLoginResponse } from '@/lib/api/controlPlaneClient';

// ─── Types ──────────────────────────────────────────────────────────

export interface ValidateInviteResponse {
  status: 'valid' | 'expired' | 'invalid';
  company_name?: string;
  inviter_name?: string;
  email?: string;
  first_name?: string;
}

/** Must match CPLoginResponse field names exactly for authStore compatibility */
export type AcceptInviteResponse = CPLoginResponse;

export interface TeamMember {
  email: string;
  first_name: string;
  full_name: string;
  role: string;
  status: string;
  app_access: string[];
  user_image: string | null;
  joined_at: string | null;
  invited_on: string | null;
  invite_key: string | null;
}

export interface ListTeamMembersResponse {
  members: TeamMember[];
  total: number;
  active: number;
  cost_per_user: number;
}

export interface InviteMemberResponse {
  success: boolean;
  email: string;
  invite_key: string;
  status: string;
}

export interface InviteLink {
  code: string;
  role: string;
  app_access: string[];
  max_uses: number;
  uses_count: number;
  expires_at: string | null;
  created_at: string;
}

export interface CreateInviteLinkResponse {
  code: string;
  url: string;
  expires_at: string | null;
  max_uses: number;
  role: string;
  app_access: string[];
}

export interface ValidateInviteCodeResponse {
  status: 'valid' | 'expired' | 'used_up' | 'invalid';
  company_name?: string;
  cost_per_user?: number;
}

// ─── Public endpoints (no auth needed) ──────────────────────────────

export async function cpValidateInvitationKey(key: string): Promise<ValidateInviteResponse> {
  return cpGuestApiCall<ValidateInviteResponse>(
    'control_plane.api.team.validate_invitation_key',
    {
      method: 'POST',
      body: JSON.stringify({ key }),
    }
  );
}

export async function cpAcceptInvitation(
  key: string,
  firstName: string,
  lastName: string,
  password: string,
): Promise<AcceptInviteResponse> {
  return cpGuestApiCall<AcceptInviteResponse>(
    'control_plane.api.team.accept_invitation',
    {
      method: 'POST',
      body: JSON.stringify({
        key,
        first_name: firstName,
        last_name: lastName,
        password,
      }),
    }
  );
}

// ─── Authenticated endpoints ────────────────────────────────────────

export async function cpListTeamMembers(): Promise<ListTeamMembersResponse> {
  const token = getCPToken();
  return cpGuestApiCall<ListTeamMembersResponse>(
    'control_plane.api.team.list_team_members',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token }),
    }
  );
}

export async function cpInviteMember(
  email: string,
  firstName: string,
  role: string,
  appAccess: string[],
): Promise<InviteMemberResponse> {
  const token = getCPToken();
  return cpGuestApiCall<InviteMemberResponse>(
    'control_plane.api.team.invite_member',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token,
        email,
        first_name: firstName,
        role,
        app_access: JSON.stringify(appAccess),
      }),
    }
  );
}

export async function cpResendInvite(email: string): Promise<{ success: boolean }> {
  const token = getCPToken();
  return cpGuestApiCall<{ success: boolean }>(
    'control_plane.api.team.resend_invite',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token, email }),
    }
  );
}

// ─── App Access endpoints ───────────────────────────────────────────

export interface MyAccessResponse {
  role: string;
  app_access: string[];
  is_admin: boolean;
  installed_apps: string[];
  /** Support-only tenant: dashboard locked to the Help Center. */
  support_only?: boolean;
}

export async function cpMyAccess(): Promise<MyAccessResponse> {
  const token = getCPToken();
  return cpGuestApiCall<MyAccessResponse>(
    'control_plane.api.team.my_access',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token }),
    }
  );
}

export async function cpRemoveMember(email: string): Promise<{ success: boolean }> {
  const token = getCPToken();
  return cpGuestApiCall<{ success: boolean }>(
    'control_plane.api.team.remove_member',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token, email }),
    }
  );
}

export async function cpCancelInvite(email: string): Promise<{ success: boolean }> {
  const token = getCPToken();
  return cpGuestApiCall<{ success: boolean }>(
    'control_plane.api.team.cancel_invite',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token, email }),
    }
  );
}

export async function cpUpdateMember(
  email: string,
  role?: string,
  appAccess?: string[],
): Promise<TeamMember> {
  const token = getCPToken();
  return cpGuestApiCall<TeamMember>(
    'control_plane.api.team.update_member',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token,
        email,
        ...(role && { role }),
        ...(appAccess && { app_access: JSON.stringify(appAccess) }),
      }),
    }
  );
}

// ─── Invite Link endpoints ───────────────────────────────────────

export async function cpCreateInviteLink(
  expiresAfter: string,
  maxUses: number,
  role: string,
  appAccess: string[],
): Promise<CreateInviteLinkResponse> {
  const token = getCPToken();
  return cpGuestApiCall<CreateInviteLinkResponse>(
    'control_plane.api.team_invite_link.create_invite_link',
    {
      method: 'POST',
      body: JSON.stringify({
        access_token: token,
        expires_after: expiresAfter,
        max_uses: maxUses,
        role,
        app_access: JSON.stringify(appAccess),
      }),
    }
  );
}

export async function cpListInviteLinks(): Promise<{ links: InviteLink[] }> {
  const token = getCPToken();
  return cpGuestApiCall<{ links: InviteLink[] }>(
    'control_plane.api.team_invite_link.list_invite_links',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token }),
    }
  );
}

export async function cpRevokeInviteLink(code: string): Promise<{ success: boolean }> {
  const token = getCPToken();
  return cpGuestApiCall<{ success: boolean }>(
    'control_plane.api.team_invite_link.revoke_invite_link',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token, code }),
    }
  );
}

// ─── Public Invite Link endpoints (no auth needed) ──────────────────

export async function cpValidateInviteCode(code: string): Promise<ValidateInviteCodeResponse> {
  return cpGuestApiCall<ValidateInviteCodeResponse>(
    'control_plane.api.team_invite_link.validate_invite_code',
    {
      method: 'POST',
      body: JSON.stringify({ code }),
    }
  );
}

export async function cpJoinViaInviteLink(
  code: string,
  firstName: string,
  lastName: string,
  email: string,
  password: string,
): Promise<CPLoginResponse> {
  return cpGuestApiCall<CPLoginResponse>(
    'control_plane.api.team_invite_link.join_via_invite_link',
    {
      method: 'POST',
      body: JSON.stringify({
        code,
        first_name: firstName,
        last_name: lastName,
        email,
        password,
      }),
    }
  );
}
